import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import * as Yup from "yup";
import { Formik, Form } from "formik";
import Typography from '@material-ui/core/Typography';

import ContactNotesDialogListItem from '../ContactNotesDialogListItem';
import ConfirmationModal from '../ConfirmationModal';
import ContactNotesEditModal from '../ContactNotesEditModal';

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";

import ButtonWithSpinner from '../ButtonWithSpinner';

import useTicketNotes from '../../hooks/useTicketNotes';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    form: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    field: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            fontSize: '0.8rem',
        },
        '& .MuiOutlinedInput-inputMultiline': {
            padding: '8px 10px',
        },
        '& .MuiInputLabel-outlined': {
            fontSize: '0.8rem',
        },
    },
    actions: {
        display: 'flex',
        gap: 6,
        justifyContent: 'flex-end',
    },
    actionBtn: {
        textTransform: 'none',
        borderRadius: 8,
        minHeight: 30,
        padding: '4px 12px',
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    list: {
        width: '100%',
        maxHeight: 180,
        overflowY: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        marginTop: 2,
        ...theme.scrollbarStyles,
    },
    empty: {
        fontSize: '0.75rem',
        color: theme.palette.text.disabled,
        fontStyle: 'italic',
        padding: '6px 2px',
    },
    count: {
        fontSize: '0.7rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
}));

const NoteSchema = Yup.object().shape({
    note: Yup.string()
        .min(2, "Too Short!")
        .required("Required")
});

export function ContactNotes({ ticket }) {
    const { id: ticketId, contactId } = ticket
    const classes = useStyles()
    const [newNote, setNewNote] = useState({ note: "" });
    const [loading, setLoading] = useState(false)
    const [showOnDeleteDialog, setShowOnDeleteDialog] = useState(false)
    const [selectedNote, setSelectedNote] = useState({})
    const [notes, setNotes] = useState([])
    const { saveNote, deleteNote, listNotes } = useTicketNotes()
    const [editingNote, setEditingNote] = useState(null);

    useEffect(() => {
        async function openAndFetchData() {
            handleResetState()
            await loadNotes()
        }
        openAndFetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleResetState = () => {
        setNewNote({ note: "" })
        setLoading(false)
    }

    const handleChangeComment = (e) => {
        setNewNote({ note: e.target.value })
    }

    const handleEdit = (note) => {
        setEditingNote(note);
    };

    const handleSave = async values => {
        setLoading(true)
        try {
            await saveNote({
                ...values,
                ticketId,
                contactId
            })
            await loadNotes()
            setNewNote({ note: '' })
            toast.success('Observação adicionada com sucesso!')
        } catch (e) {
            toast.error(e)
        }
        setLoading(false)
    }

    const handleOpenDialogDelete = (item) => {
        if (item) {
            setSelectedNote(item);
            setShowOnDeleteDialog(true);
        }
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await deleteNote(selectedNote.id)
            await loadNotes()
            setSelectedNote({})
            toast.success('Observação excluída com sucesso!')
        } catch (e) {
            toast.error(e)
        }
        setLoading(false)
    }

    const loadNotes = async () => {
        setLoading(true)
        try {
            const notes = await listNotes({ ticketId, contactId })
            setNotes(notes)
        } catch (e) {
            toast.error(e)
        }
        setLoading(false)
    }

    return (
        <div className={classes.root}>
            <ContactNotesEditModal
                open={editingNote !== null}
                onClose={() => setEditingNote(null)}
                note={editingNote ? editingNote.note : ''}
                onSave={handleSave}
            />
            <ConfirmationModal
                title="Excluir Registro"
                open={showOnDeleteDialog}
                onClose={setShowOnDeleteDialog}
                onConfirm={handleDelete}
            >
                Deseja realmente excluir este registro?
            </ConfirmationModal>
            <Formik
                initialValues={newNote}
                enableReinitialize={true}
                validationSchema={NoteSchema}
                onSubmit={(values, actions) => {
                    setTimeout(() => {
                        handleSave(values);
                        actions.setSubmitting(false);
                    }, 400);
                }}
            >
                {({ touched, errors, setErrors, setFieldValue, values, handleBlur, handleSubmit, isSubmitting }) => (
                    <Form
                        className={classes.form}
                        onSubmit={(e) => {
                            e.stopPropagation();
                            handleSubmit(e);
                        }}
                    >
                        <TextField
                            name="note"
                            rows={2}
                            label={i18n.t("ticketOptionsMenu.appointmentsModal.textarea")}
                            placeholder={i18n.t("ticketOptionsMenu.appointmentsModal.placeholder")}
                            multiline
                            error={touched.note && Boolean(errors.note)}
                            helperText={touched.note && errors.note}
                            variant="outlined"
                            margin="dense"
                            className={classes.field}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                handleChangeComment(e);
                                setFieldValue("note", newValue);
                            }}
                            onBlur={handleBlur}
                            value={values.note || ""}
                            fullWidth
                        />

                        <div className={classes.actions}>
                            <Button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setNewNote({ note: "" });
                                    setFieldValue("note", "");
                                    setErrors({});
                                    handleChangeComment({ target: { value: "" } });
                                }}
                                color="primary"
                                variant="outlined"
                                size="small"
                                className={classes.actionBtn}
                            >
                                Cancelar
                            </Button>
                            <ButtonWithSpinner
                                loading={loading || isSubmitting}
                                color="primary"
                                type="submit"
                                variant="contained"
                                size="small"
                                className={classes.actionBtn}
                                disabled={loading || isSubmitting}
                            >
                                Salvar
                            </ButtonWithSpinner>
                        </div>

                        {notes.length > 0 ? (
                            <>
                                <Typography className={classes.count}>
                                    {notes.length} observação(ões) — clique para expandir
                                </Typography>
                                <div className={classes.list}>
                                    {notes.map((note) => (
                                        <ContactNotesDialogListItem
                                            note={note}
                                            key={note.id}
                                            deleteItem={handleOpenDialogDelete}
                                            editItem={() => handleEdit(note)}
                                        />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <Typography className={classes.empty}>
                                Nenhuma observação
                            </Typography>
                        )}
                    </Form>
                )}
            </Formik>
        </div>
    );
}
